import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext.jsx";
import { login, register } from "@/api/public.api.js";
import PhoneFields from "@/components/PhoneFields.jsx";
import { MAIN_ROUTE, ROLES } from "@/utils/constants.js";
import { CUSTOMER_TYPES, EMPTY_PHONE } from "@/utils/customerProfile.js";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/bodyScrollLock.js";
import "./login-modal.css";

export default function LoginModal({ onClose, initialMode = "login", returnTo = MAIN_ROUTE }) {
    const [mode, setMode] = useState(initialMode);
    const [showPassword, setShowPassword] = useState(false);
    const [form, setForm] = useState({
        username: "",
        password: "",
        firstName: "",
        lastName: "",
        email: "",
        customerType: CUSTOMER_TYPES.INDIVIDUAL,
        companyName: "",
        registrationNumber: "",
        taxId: "",
        phones: [{ ...EMPTY_PHONE }]
    });
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const { login: authLogin } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        lockBodyScroll();
        return () => {
            unlockBodyScroll();
        };
    }, []);

    const handleChange = (event) => {
        setForm({ ...form, [event.target.name]: event.target.value });
    };

    const handleCustomerTypeChange = (customerType) => {
        setForm({
            ...form,
            customerType,
            companyName: customerType === CUSTOMER_TYPES.CORPORATION ? form.companyName : "",
            registrationNumber: customerType === CUSTOMER_TYPES.CORPORATION ? form.registrationNumber : "",
            taxId: customerType === CUSTOMER_TYPES.CORPORATION ? form.taxId : ""
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (submitting) {
            return;
        }
        setError("");
        setSubmitting(true);
        try {
            if (mode === "login") {
                const data = await login({
                    username: form.username,
                    password: form.password,
                    role: ROLES.CUSTOMER
                });
                authLogin(ROLES.CUSTOMER, data.token);
                navigate(returnTo || MAIN_ROUTE, { replace: true });
            } else {
                await register({
                    username: form.username,
                    password: form.password,
                    firstName: form.firstName,
                    lastName: form.lastName,
                    email: form.email.trim() || undefined,
                    customerType: form.customerType,
                    companyName: form.customerType === CUSTOMER_TYPES.CORPORATION ? form.companyName : undefined,
                    registrationNumber: form.customerType === CUSTOMER_TYPES.CORPORATION ? form.registrationNumber || undefined : undefined,
                    taxId: form.customerType === CUSTOMER_TYPES.CORPORATION ? form.taxId || undefined : undefined,
                    phones: form.phones.map((phone) => ({
                        phoneNumber: phone.phoneNumber.trim(),
                        label: phone.label.trim() || undefined
                    }))
                });
                const data = await login({
                    username: form.username,
                    password: form.password,
                    role: ROLES.CUSTOMER
                });
                authLogin(ROLES.CUSTOMER, data.token);
                navigate(returnTo || MAIN_ROUTE, { replace: true });
            }
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return createPortal(
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal card login-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{mode === "login" ? "Connexion" : "Créer un compte"}</h2>
                    <button className="link-btn" onClick={onClose}>Fermer</button>
                </div>
                <form onSubmit={handleSubmit}>
                    {mode === "register" && (
                        <>
                            <fieldset className="customer-type-fieldset">
                                <legend>Type de compte</legend>
                                <label className="customer-type-option">
                                    <input
                                        type="radio"
                                        name="customerType"
                                        value={CUSTOMER_TYPES.INDIVIDUAL}
                                        checked={form.customerType === CUSTOMER_TYPES.INDIVIDUAL}
                                        onChange={() => handleCustomerTypeChange(CUSTOMER_TYPES.INDIVIDUAL)}
                                    />
                                    Particulier
                                </label>
                                <label className="customer-type-option">
                                    <input
                                        type="radio"
                                        name="customerType"
                                        value={CUSTOMER_TYPES.CORPORATION}
                                        checked={form.customerType === CUSTOMER_TYPES.CORPORATION}
                                        onChange={() => handleCustomerTypeChange(CUSTOMER_TYPES.CORPORATION)}
                                    />
                                    Entreprise
                                </label>
                            </fieldset>

                            {form.customerType === CUSTOMER_TYPES.CORPORATION && (
                                <>
                                    <label className="field">
                                        Raison sociale
                                        <input
                                            name="companyName"
                                            value={form.companyName}
                                            onChange={handleChange}
                                            required
                                        />
                                    </label>
                                    <label className="field">
                                        N° RCCM (optionnel)
                                        <input
                                            name="registrationNumber"
                                            value={form.registrationNumber}
                                            onChange={handleChange}
                                        />
                                    </label>
                                    <label className="field">
                                        NIU (optionnel)
                                        <input
                                            name="taxId"
                                            value={form.taxId}
                                            onChange={handleChange}
                                        />
                                    </label>
                                </>
                            )}

                            <label className="field">
                                {form.customerType === CUSTOMER_TYPES.CORPORATION ? "Prénom du contact" : "Prénom"}
                                <input name="firstName" value={form.firstName} onChange={handleChange} required />
                            </label>
                            <label className="field">
                                {form.customerType === CUSTOMER_TYPES.CORPORATION ? "Nom du contact" : "Nom"}
                                <input name="lastName" value={form.lastName} onChange={handleChange} required />
                            </label>
                            <label className="field">
                                E-mail (optionnel)
                                <input
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    maxLength={255}
                                    autoComplete="email"
                                />
                            </label>

                            <PhoneFields
                                phones={form.phones}
                                onChange={(phones) => setForm({ ...form, phones })}
                                idPrefix="register-phone"
                            />
                        </>
                    )}
                    <label className="field">
                        Nom d'utilisateur
                        <input
                            name="username"
                            type="text"
                            value={form.username}
                            onChange={handleChange}
                            minLength={3}
                            maxLength={50}
                            required
                        />
                    </label>
                    <label className="field">
                        Mot de passe
                        <div className="password-field">
                            <input
                                name="password"
                                type={showPassword ? "text" : "password"}
                                value={form.password}
                                onChange={handleChange}
                                minLength={3}
                                maxLength={50}
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? "Masquer" : "Afficher"}
                            </button>
                        </div>
                    </label>
                    {error && <p className="error">{error}</p>}
                    <button type="submit" className="btn btn-primary full-width" disabled={submitting}>
                        {submitting
                            ? (mode === "login" ? "Connexion..." : "Inscription...")
                            : (mode === "login" ? "Se connecter" : "S'inscrire")}
                    </button>
                </form>
                <button className="link-btn switch-mode" onClick={() => setMode(mode === "login" ? "register" : "login")}>
                    {mode === "login" ? "Pas encore de compte ? S'inscrire" : "Déjà un compte ? Se connecter"}
                </button>
            </div>
        </div>,
        document.body
    );
}
